import { Flex, Text } from '@chakra-ui/react';
import { TopRightItemType, TopRightType } from '@llx/common';
import { Slide } from '@llx/common';
import { useMemo } from 'react';

import { useListAnswersQuery } from '@/@generated/codegen/API';
import { useLoggedInUserAuthContext } from '@/components/AuthTokenContextProvider/UserAuthContext';
import { useCurrentMeetingDataContext } from '@/features/meeting/context/CurrentMeetingDataContext';
import { NoUserAnswer } from '@/utils/errors/NoUserAnswer';
import { captureSentry } from '@/utils/helpers/sentryHelper';

import getAnswerItemsAccordingToUserAnswer from './utils/getAnswerItemsAccordingToUserAnswer';

type Props = {
  item: TopRightItemType;
  slides: Slide[];
};

function TopRightItem({ slides, item }: Props) {
  const {
    currentMeeting: meeting,
    currentPatientCaseId,
    isPreview,
  } = useCurrentMeetingDataContext();
  const { userID } = useLoggedInUserAuthContext();

  const answersQuery = useListAnswersQuery({
    variables: {
      paginationArgs: {
        first: 100,
      },
      where: {
        meetingId: {
          equals: meeting.id,
        },
        slideId: {
          equals: item.type === TopRightType.QUESTION_ANSWER ? item.questionId : undefined,
        },
      },
    },
    skip:
      !meeting ||
      !currentPatientCaseId ||
      item.type !== TopRightType.QUESTION_ANSWER ||
      !item.questionId ||
      isPreview,
  });
  const data = useMemo(() => {
    if (item.type === TopRightType.TEXT) {
      return item.title;
    }
    if (item.type === TopRightType.QUESTION_ANSWER) {
      //fetch the user's answer to that slideId
      if (!meeting || !currentPatientCaseId || !userID) {
        //NOTE: It will be null if it is a preview
        if (!isPreview) {
          captureSentry({
            title: 'TopRightItem - meeting or currentCaseId or userID is undefined',
            detail: {
              meeting,
              currentPatientCaseId: currentPatientCaseId,
              userID,
            },
          });
        }
        return item.defaultTitle;
      }
      const questionID = item.questionId;
      const allAnswers = answersQuery.data?.listAnswers.edges.map((edge) => edge.node) || [];
      //NOTE: If ever a user can send multiple answers, change this
      try {
        const answerItems = getAnswerItemsAccordingToUserAnswer(
          userID,
          item.questionId,
          allAnswers,
          slides
        );
        return answerItems[0].title;
      } catch (e) {
        //This is an error I don't want to capture
        if (e instanceof NoUserAnswer) {
          return item.defaultTitle;
        }

        captureSentry({
          title: 'Error getting answer item according to user answer',
          error: e,
          detail: { userID, questionID, allAnswers },
        });
        return item.defaultTitle;
      }
    }
    return 'N/A';
  }, [item, answersQuery.data, meeting, currentPatientCaseId, userID, slides, isPreview]);

  const loading = answersQuery.loading;

  return (
    <Flex
      background="livelinx.purple100"
      width="250px"
      height="80px"
      borderRadius="10px"
      alignItems="center"
      justifyContent="center"
      padding="20px"
      margin="10px"
    >
      <Text color="white" fontSize="20px" noOfLines={2} textAlign="center">
        {loading ? 'Loading...' : data}
      </Text>
    </Flex>
  );
}

export default TopRightItem;
